import { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@lib/context/AuthContextType';
import { getPortalMe, updatePortalProfile, changePortalPassword, getClientProfile, updateClientProfile } from '@lib/api/portal';

const PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

const CONTACT_OPTIONS = [
  { value: 'phone', label: 'Phone call', icon: 'phone' },
  { value: 'email', label: 'Email',      icon: 'email' },
  { value: 'text',  label: 'Text / SMS', icon: 'sms' },
];

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40';

export const ProfilePage = () => {
  const { user, setSession } = useContext(AuthContext)!;
  const isClient = user?.role === 'client';

  const [username, setUsername] = useState(user?.username || '');
  const [phone, setPhone] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileErr, setProfileErr] = useState('');

  // Client-specific fields
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingProvince, setBillingProvince] = useState('');
  const [billingPostal, setBillingPostal] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [preferredContact, setPreferredContact] = useState('');

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwErr, setPwErr] = useState('');

  useEffect(() => {
    getPortalMe().then(u => {
      setUsername(u.username || '');
      setPhone(u.phone || '');
    }).catch(() => {});
    if (isClient) {
      getClientProfile().then(c => {
        if (!c) return;
        setCompany(c.company || '');
        setAddress(c.address || '');
        setBillingCity(c.billing_city || '');
        setBillingProvince(c.billing_province || '');
        setBillingPostal(c.billing_postal || '');
        setSiteAddress(c.site_address || '');
        setPreferredContact(c.preferred_contact || '');
        if (!phone && c.phone) setPhone(c.phone);
      }).catch(() => {});
    }
  }, [isClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileBusy(true);
    setProfileMsg('');
    setProfileErr('');
    try {
      await updatePortalProfile({ username: username.trim(), phone: phone.trim() || undefined });
      if (isClient) {
        await updateClientProfile({
          name: username.trim(),
          company: company.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          billing_city: billingCity.trim() || undefined,
          billing_province: billingProvince || undefined,
          billing_postal: billingPostal.trim() || undefined,
          site_address: siteAddress.trim() || undefined,
          preferred_contact: preferredContact || undefined,
        });
      }
      setProfileMsg('Profile updated.');
      if (user) {
        const stored = localStorage.getItem('accessToken');
        const refresh = localStorage.getItem('refreshToken');
        if (stored && refresh) {
          setSession(stored, refresh, { ...user, username: username.trim() });
        }
      }
    } catch {
      setProfileErr('Failed to update profile.');
    } finally {
      setProfileBusy(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwErr('Passwords do not match.'); return; }
    if (newPw.length < 8) { setPwErr('Password must be at least 8 characters.'); return; }
    setPwBusy(true);
    setPwMsg('');
    setPwErr('');
    try {
      await changePortalPassword(currentPw, newPw);
      setPwMsg('Password changed successfully.');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setPwErr(msg || 'Failed to change password.');
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[#2C1810]">My Account</h1>
        <p className="text-sm text-gray-500 mt-0.5">{user?.email}</p>
      </div>

      {/* Profile info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#C77D3F] text-xl">person</span>
          Profile
        </h2>
        {profileMsg && <p className="text-sm text-green-700 mb-3">{profileMsg}</p>}
        {profileErr && <p className="text-sm text-red-600 mb-3">{profileErr}</p>}
        <form onSubmit={saveProfile} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {isClient ? 'Full Name' : 'Display Name'}
            </label>
            <input type="text" value={username} onChange={e => setUsername(e.target.value)} className={inputCls} />
          </div>
          {isClient && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Company / Business Name</label>
              <input type="text" value={company} onChange={e => setCompany(e.target.value)}
                placeholder="Optional" className={inputCls} />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="(250) 555-0100" className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input type="email" value={user?.email || ''} disabled
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 cursor-not-allowed" />
            <p className="text-xs text-gray-400 mt-1">Email cannot be changed. Contact OchrePoint if needed.</p>
          </div>

          {isClient && (
            <>
              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wide">Billing Address</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Street Address</label>
                    <input type="text" value={address} onChange={e => setAddress(e.target.value)}
                      placeholder="123 Main Street" className={inputCls} />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                      <input type="text" value={billingCity} onChange={e => setBillingCity(e.target.value)}
                        placeholder="Duncan" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Province</label>
                      <select value={billingProvince} onChange={e => setBillingProvince(e.target.value)} className={inputCls}>
                        <option value="">—</option>
                        {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Postal Code</label>
                    <input type="text" value={billingPostal} onChange={e => setBillingPostal(e.target.value)}
                      placeholder="V9L 1A1" className={`${inputCls} max-w-[140px]`} />
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Site / Project Address <span className="text-gray-400">(if different from billing)</span>
                </label>
                <textarea value={siteAddress} onChange={e => setSiteAddress(e.target.value)}
                  rows={2} placeholder="Leave blank if same as billing" className={`${inputCls} resize-none`} />
              </div>

              <div className="pt-2 border-t border-gray-100">
                <label className="block text-xs font-medium text-gray-600 mb-2">Preferred Contact Method</label>
                <div className="flex gap-2 flex-wrap">
                  {CONTACT_OPTIONS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setPreferredContact(preferredContact === opt.value ? '' : opt.value)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        preferredContact === opt.value
                          ? 'border-[#C77D3F] bg-[#C77D3F]/10 text-[#C77D3F]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">{opt.icon}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* #19 – sticky save button (position:sticky above bottom nav) */}
          <div className="sticky bottom-20 pt-1">
            <button type="submit" disabled={profileBusy}
              className="w-full bg-[#C77D3F] text-white rounded-xl px-5 py-3 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50 active:scale-95 transition-transform shadow-md">
              {profileBusy ? 'Saving…' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Notification settings — role-aware (audit: clients were sent to the
          employee route and bounced back home with no explanation) */}
      <Link
        to={isClient ? '/portal/client/notifications' : '/portal/employee/notifications'}
        className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-gray-50"
      >
        <span className="material-symbols-outlined text-[#C77D3F] text-xl">notifications</span>
        <span className="flex-1 text-sm font-semibold text-gray-900">Notification settings</span>
        <span className="material-symbols-outlined text-gray-300">chevron_right</span>
      </Link>

      {/* Portal help */}
      <Link
        to={isClient ? '/portal/client/help' : '/portal/employee/help'}
        className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 active:bg-gray-50"
      >
        <span className="material-symbols-outlined text-[#C77D3F] text-xl">help</span>
        <span className="flex-1 text-sm font-semibold text-gray-900">Help — how to use the portal</span>
        <span className="material-symbols-outlined text-gray-300">chevron_right</span>
      </Link>

      {/* Change password */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-[#C77D3F] text-xl">lock</span>
          Change Password
        </h2>
        {pwMsg && <p className="text-sm text-green-700 mb-3">{pwMsg}</p>}
        {pwErr && <p className="text-sm text-red-600 mb-3">{pwErr}</p>}
        {/* #19 – Enter on last field submits */}
        <form onSubmit={changePassword} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Current Password</label>
            <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
              required className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Password</label>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
              required minLength={8} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New Password</label>
            <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              required
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); changePassword(e as unknown as React.FormEvent); } }}
              className={inputCls} />
          </div>
          <button type="submit" disabled={pwBusy}
            className="w-full bg-[#C77D3F] text-white rounded-xl px-5 py-3 text-sm font-semibold hover:bg-[#b36e35] disabled:opacity-50 active:scale-95 transition-transform">
            {pwBusy ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};
